import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useAuth } from "../../app/AuthContext";
import { api, ApiError } from "../../shared/api/client";
import type { EnvironmentDetail } from "../../shared/types/api";
import {
  Button,
  DataTable,
  InlineAlert,
  PageHeader,
  StatusBadge,
  type Column,
} from "../../shared/ui";
import { EnvironmentDrawer } from "./EnvironmentDrawer";
import { EnvironmentGatesDrawer } from "./EnvironmentGatesDrawer";

/**
 * The environments releases go to (§63).
 *
 * <p>Configuration rather than a hardcoded list, which is what §63 asked for and what the seeded
 * five only looked like. A customer with two production regions or an environment per client can
 * add what they actually have, and the production flag — not the name — is what makes an
 * environment get treated as production.
 *
 * <p>Withdrawn environments are shown rather than hidden. They are still attached to every past
 * deployment that used them, and a list that quietly dropped them would make somebody wonder
 * where an environment in the history had gone.
 */
export function EnvironmentsPage() {
  const { can } = useAuth();
  const [editing, setEditing] = useState<EnvironmentDetail | null>(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [editingGates, setEditingGates] = useState<EnvironmentDetail | null>(null);
  const queryClient = useQueryClient();

  const environments = useQuery({
    queryKey: ["environments", "all"],
    queryFn: () => api.get<EnvironmentDetail[]>("/api/v1/environments/all"),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["environments"] });
    void queryClient.invalidateQueries({ queryKey: ["release-environments"] });
  };

  const setOffered = async (environment: EnvironmentDetail, offered: boolean) => {
    setBusy(environment.id);
    setError(null);
    try {
      await api.post(
        `/api/v1/environments/${environment.id}/${offered ? "reinstate" : "withdraw"}`,
      );
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not do that.");
    } finally {
      setBusy(null);
    }
  };

  const columns: Column<EnvironmentDetail>[] = [
    {
      key: "name",
      header: "Environment",
      render: (environment) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-ink-900">{environment.name}</p>
          <p className="font-mono text-xs text-ink-400">{environment.code}</p>
        </div>
      ),
    },
    {
      key: "description",
      header: "Description",
      render: (environment) =>
        environment.description ?? <span className="text-ink-400">—</span>,
    },
    {
      key: "production",
      header: "Treated as",
      render: (environment) => (
        <StatusBadge tone={environment.production ? "red" : "gray"}>
          {environment.production ? "Production" : "Non-production"}
        </StatusBadge>
      ),
    },
    {
      key: "displayOrder",
      header: "Order",
      render: (environment) => <span className="tnum">{environment.displayOrder}</span>,
    },
    {
      key: "active",
      header: "Status",
      render: (environment) => (
        <StatusBadge tone={environment.active ? "green" : "gray"}>
          {environment.active ? "Offered" : "Withdrawn"}
        </StatusBadge>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (environment) =>
        can("ENVIRONMENT_MANAGE") ? (
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              title="What a release must satisfy before it may be deployed here"
              onClick={(event) => {
                event.stopPropagation();
                setEditingGates(environment);
              }}
            >
              Requirements
            </Button>
            <Button
              variant="ghost"
              size="sm"
              loading={busy === environment.id}
              onClick={(event) => {
                event.stopPropagation();
                void setOffered(environment, !environment.active);
              }}
            >
              {environment.active ? "Withdraw" : "Offer again"}
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <>
      <PageHeader
        title="Environments"
        subtitle="Where releases are deployed, and what a release must satisfy to reach each one. A sandbox should ask far less than production."
        actions={
          can("ENVIRONMENT_MANAGE") && (
            <Button icon={<Plus className="h-4 w-4" />} onClick={() => setAdding(true)}>
              New environment
            </Button>
          )
        }
      />

      {error && (
        <div className="mb-3">
          <InlineAlert tone="error">{error}</InlineAlert>
        </div>
      )}

      <DataTable
        columns={columns}
        rows={environments.data ?? []}
        rowKey={(environment) => environment.id}
        loading={environments.isPending}
        onRowClick={can("ENVIRONMENT_MANAGE") ? setEditing : undefined}
        emptyTitle="No environments"
        emptyMessage="Releases need somewhere to go — development, staging, production, or whatever your estate actually looks like."
      />

      <EnvironmentGatesDrawer
        environment={editingGates}
        open={editingGates != null}
        onClose={() => setEditingGates(null)}
        onSaved={refresh}
      />
      <EnvironmentDrawer
        environment={editing}
        open={adding || editing != null}
        onClose={() => {
          setAdding(false);
          setEditing(null);
        }}
        onSaved={refresh}
      />
    </>
  );
}
